import { TextField as PixieTextField } from "@pacificoseguros/pixie/react/textfield";
import React, { ReactNode, Ref, useCallback, useMemo, useState } from "react";
import { UseFormMethods } from "react-hook-form";
import { FlexLayout } from "../../../components/layout/flex-layout";
import { SpacingBox } from "../../../components/layout/spacing-box";
import { currencyMaskFunction } from "../../../constants/mask-functions";
import {
    alphaNumericregex,
    currencyAmountRegex,
    onlyAlphaRegex,
    onlyNumericRegex,
} from "../../../constants/regex";
import { omit } from "../../../libs/utils/object";
import { FieldError } from "./field-error";

type TextFieldProps = TextFieldNS.TextFieldProps & {
    fieldDefinition: FieldDefinition;
    inputRef?: Ref<any>;
    rhForm: UseFormMethods<any>;
    renderLabel?: (label: ReactNode) => ReactNode;
    maskFn?: (val: string) => string;
    hideError?: boolean;
    errorMessage?: string;
    touched?: boolean;
    hideLabel?: boolean;
};

export const TextField: React.FC<TextFieldProps> = (props: TextFieldProps) => {
    const [lastValidValue, setLastValidValue] = useState();
    const { isAlpha, isAlphaNumeric, isNumeric, isCurrency } =
        props.fieldDefinition;
    const pixieTextFieldProps = omit(props, [
        "fieldDefinition",
        "inputRef",
        "rhForm",
        "renderLabel",
        "maskFn",
        "touched",
        "errorMessage",
        "hideError",
        "hideLabel",
    ]);
    const maskFn = useMemo(() => {
        if (props.fieldDefinition.isCurrency) {
            return currencyMaskFunction;
        }
        return props.maskFn;
    }, []);
    const inputFormatRegex = useMemo(() => {
        if (isAlpha) return onlyAlphaRegex;
        else if (isAlphaNumeric) return alphaNumericregex;
        else if (isNumeric) return onlyNumericRegex;
        else if (isCurrency) return currencyAmountRegex;
        return null;
    }, [isAlpha, isAlphaNumeric, isNumeric]);
    const handleInput = useCallback(
        (event) => {
            const { value } = event.target;
            const maskedValue = maskFn ? maskFn(value) : value;
            let valueToSet = maskedValue;
            if (!inputFormatRegex) {
                setLastValidValue(maskedValue);
                return;
            }
            if (inputFormatRegex.test(maskedValue) || !maskedValue) {
                setLastValidValue(maskedValue);
            } else if (!!maskedValue) {
                valueToSet = lastValidValue;
            }
            props.rhForm.setValue(props.fieldDefinition.key, valueToSet, {
                shouldValidate: true,
            });
        },
        [inputFormatRegex, lastValidValue, name, maskFn]
    );
    const refHandler = props.inputRef || props.rhForm?.register;
    const label = (
        <label
            htmlFor={props.fieldDefinition.key}
            style={{ color: "var(--secondary)", whiteSpace: "pre-line" }}
        >
            <b>{props.fieldDefinition.label || <>&nbsp;</>}</b>
        </label>
    );
    const touched =
        props.touched ||
        props.rhForm.formState.touched[props.fieldDefinition.key];
    const error = props.rhForm?.errors[props.fieldDefinition.key];
    const hasError = !!props.errorMessage || !!error;
    const errorMessage = props.errorMessage || error?.message;
    return (
        <FlexLayout direction="column">
            {props.renderLabel
                ? props.renderLabel(label)
                : !props.hideLabel && label}
            <SpacingBox mb={4} />
            <PixieTextField
                {...pixieTextFieldProps}
                name={props.fieldDefinition.key}
                id={props.fieldDefinition.key}
                placeholder={props.fieldDefinition.placeholder}
                ref={refHandler}
                onInput={handleInput}
                invalid={props.invalid || (!!error && touched)}
                maxLength={props.fieldDefinition.maxLength}
            />
            {hasError && touched && !props.hideError && (
                <FieldError message={errorMessage} />
            )}
        </FlexLayout>
    );
};
